import { useTranslate } from 'module/hooks/use-translation';
import { Tag } from 'module/ui/components/atoms/tag';
import { Checkbox } from 'antd';
import {
  SignalRow,
  SignalRowNoBackground,
  SignalReasonsContainer,
} from './styles';

interface SignalData {
  signalConfigurationId: number;
  signalName: string;
  fields: any[];
}

interface SignalItemProps {
  signal: SignalData;
  selectedSignals: string[];
  getAllReasonsForSignal: (signal: SignalData) => any[];
  getSelectedReasonsForSignal: (signalId: string) => number[];
  onSignalToggle: (signalId: string, reasonValues?: number[]) => void;
  onReasonToggle: (signalId: string, reasonValues: number[]) => void;
}

const handleReasonCheckboxChange = (
  selectedSignals: string[],
  selectedReasons: number[],
  reasonValue: number,
  signalId: string,
  onReasonToggle: (signalId: string, reasonValues: number[]) => void,
) => {
  const newSelectedReasons = selectedReasons.includes(reasonValue)
    ? selectedReasons.filter((r) => r !== reasonValue)
    : [...selectedReasons, reasonValue];

  onReasonToggle(signalId, newSelectedReasons);
};

export const SignalItem: React.FC<SignalItemProps> = ({
  signal,
  selectedSignals,
  getAllReasonsForSignal,
  getSelectedReasonsForSignal,
  onSignalToggle,
  onReasonToggle,
}) => {
  const signalId = signal.signalConfigurationId.toString();
  const reasons = getAllReasonsForSignal(signal);
  const translate = useTranslate();
  let selectedReasons: number[] = [];

  if (selectedSignals.includes(signalId)) {
    selectedReasons = getSelectedReasonsForSignal(signalId);
    if (selectedReasons.length === 0) {
      selectedReasons = reasons.map((r: any) => r.value);
    }
  } else {
    selectedReasons = [];
  }
  const allReasonsSelected =
    reasons.length > 0 && selectedReasons.length === reasons.length;
  const someReasonsSelected =
    reasons.length > 0 &&
    selectedReasons.length > 0 &&
    selectedReasons.length < reasons.length;

  return (
    <>
      <SignalRow
        key={signalId}
        selected={selectedSignals.includes(signalId)}
        onClick={(e) => {
          e.stopPropagation();
          onSignalToggle(signalId);
        }}
      >
        <Checkbox
          checked={selectedSignals.includes(signalId)}
          indeterminate={someReasonsSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSignalToggle(
              signalId,
              reasons.map((r: any) => r.value),
            );
          }}
        />
        <span className="signal-name">{signal.signalName}</span>
      </SignalRow>
      {reasons.length > 0 && selectedSignals.includes(signalId) && (
        <SignalReasonsContainer>
          <SignalRowNoBackground
            selected={allReasonsSelected}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={allReasonsSelected || selectedSignals.includes(signalId)}
              indeterminate={someReasonsSelected}
              onChange={() =>
                onSignalToggle(
                  signalId,
                  reasons.map((r: any) => r.value),
                )
              }
            />
            <span>{translate('all_signals.dropdown.all_reasons')}</span>
          </SignalRowNoBackground>
          {reasons.map((reason: any) => (
            <SignalRowNoBackground
              key={reason.value}
              selected={selectedReasons.includes(reason.value)}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={selectedReasons.includes(reason.value)}
                onChange={() => {
                  handleReasonCheckboxChange(
                    selectedSignals,
                    selectedReasons,
                    reason.value,
                    signalId,
                    onReasonToggle,
                  );
                }}
              />
              <Tag
                color={reason.color}
                aiGenerated={reason.fieldOptionMeta?.isAiGenerated}
              >
                {reason.label}
              </Tag>
            </SignalRowNoBackground>
          ))}
        </SignalReasonsContainer>
      )}
    </>
  );
};
